import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Settings() {
  const [settings, setSettings] = useState({ outlet_name: "", tax_rate: 0, currency: "$" });
  const [staff, setStaff] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [s, st, ol] = await Promise.all([
      api.get("/settings"),
      api.get("/staff"),
      api.get("/outlets"),
    ]);
    setSettings(s.data);
    setStaff(st.data);
    setOutlets(ol.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.patch("/settings", {
        outlet_name: settings.outlet_name,
        tax_rate: settings.tax_rate,
        currency: settings.currency,
        auto_print_receipt: !!settings.auto_print_receipt,
        auto_print_kot: !!settings.auto_print_kot,
        swiggy_commission_pct: parseFloat(settings.swiggy_commission_pct) || 0,
        zomato_commission_pct: parseFloat(settings.zomato_commission_pct) || 0,
        swiggy_markup_pct: parseFloat(settings.swiggy_markup_pct) || 0,
        zomato_markup_pct: parseFloat(settings.zomato_markup_pct) || 0,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const saveStaff = async () => {
    if (!editing.name) return toast.error("Name required");
    if (editing.role !== "super_admin" && !editing.outlet_id) {
      return toast.error("Please select an outlet for this staff");
    }
    try {
      if (editing.id) {
        const payload = { name: editing.name, role: editing.role };
        if (editing.pin) payload.pin = editing.pin;
        await api.patch(`/staff/${editing.id}`, payload);
      } else {
        if (!editing.pin) return toast.error("PIN required");
        await api.post("/staff", { name: editing.name, role: editing.role, pin: editing.pin });
      }
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const delStaff = async (id) => {
    if (!window.confirm("Delete staff?")) return;
    await api.delete(`/staff/${id}`);
    load();
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine">
        <h1 className="font-display text-3xl font-bold">Settings</h1>
        <p className="text-sm text-boba-inkSoft mt-1">Outlet & staff</p>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 space-y-6">
        <div className="bg-white border border-boba-stoneLine rounded-md p-6">
          <h2 className="font-display text-xl font-bold mb-4">Outlet</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Outlet Name</label>
              <input value={settings.outlet_name} onChange={(e) => setSettings({ ...settings, outlet_name: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="outlet-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Tax Rate</label>
              <input type="number" step="0.001" value={settings.tax_rate} onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="outlet-tax" />
              <div className="text-[11px] text-boba-inkSoft mt-1">e.g. 0.08 = 8%</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Currency</label>
              <input value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="outlet-currency" />
            </div>
          </div>
          <button onClick={save} className="mt-4 h-11 px-5 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="settings-save">Save</button>
        </div>

        <div className="bg-white border border-boba-stoneLine rounded-md p-6">
          <h2 className="font-display text-xl font-bold mb-1">Printing</h2>
          <p className="text-xs text-boba-inkSoft mb-4">Auto-print receipts and kitchen tickets after each action</p>
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border border-boba-stoneLine rounded-md cursor-pointer hover:bg-stone-50">
              <input
                type="checkbox"
                checked={!!settings.auto_print_receipt}
                onChange={(e) => setSettings({ ...settings, auto_print_receipt: e.target.checked })}
                className="mt-1"
                data-testid="auto-print-receipt"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Auto-print receipt after payment</div>
                <div className="text-xs text-boba-inkSoft mt-0.5">Receipt is sent to the printer automatically once the payment is settled.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-boba-stoneLine rounded-md cursor-pointer hover:bg-stone-50">
              <input
                type="checkbox"
                checked={!!settings.auto_print_kot}
                onChange={(e) => setSettings({ ...settings, auto_print_kot: e.target.checked })}
                className="mt-1"
                data-testid="auto-print-kot"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Auto-print KOT when saved</div>
                <div className="text-xs text-boba-inkSoft mt-0.5">Every saved KOT is printed to the kitchen immediately.</div>
              </div>
            </label>
          </div>
          <p className="text-[11px] text-boba-inkSoft mt-3 leading-snug">
            Tip: For silent printing without the OS dialog, set your thermal printer as default and enable Chrome&apos;s <code className="font-mono">--kiosk-printing</code> flag, or use your printer&apos;s silent-print driver.
          </p>
          <button onClick={save} className="mt-4 h-11 px-5 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="settings-save-print">Save</button>
        </div>

        <div className="bg-white border border-boba-stoneLine rounded-md p-6" data-testid="aggregator-settings">
          <h2 className="font-display text-xl font-bold mb-1">Aggregator Commissions</h2>
          <p className="text-xs text-boba-inkSoft mb-4">
            Applied automatically to every Swiggy / Zomato order created at the POS. Deducted from Net Profit in the P&amp;L report.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FC8019]" /> Swiggy Commission %
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.swiggy_commission_pct ?? 0}
                onChange={(e) => setSettings({ ...settings, swiggy_commission_pct: e.target.value })}
                className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                data-testid="swiggy-commission-pct"
                placeholder="e.g. 23"
              />
              <div className="text-[11px] text-boba-inkSoft mt-1">Typical: 22–25%. Enter 0 to track gross revenue only.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#E23744]" /> Zomato Commission %
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.zomato_commission_pct ?? 0}
                onChange={(e) => setSettings({ ...settings, zomato_commission_pct: e.target.value })}
                className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                data-testid="zomato-commission-pct"
                placeholder="e.g. 24"
              />
              <div className="text-[11px] text-boba-inkSoft mt-1">Typical: 22–25%. Applied to Order Total.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FC8019]" /> Swiggy Markup %
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.swiggy_markup_pct ?? 0}
                onChange={(e) => setSettings({ ...settings, swiggy_markup_pct: e.target.value })}
                className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                data-testid="swiggy-markup-pct"
                placeholder="e.g. 30"
              />
              <div className="text-[11px] text-boba-inkSoft mt-1">
                Auto-inflates Base Price → Swiggy Price when item has no explicit Swiggy Price.
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#E23744]" /> Zomato Markup %
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.zomato_markup_pct ?? 0}
                onChange={(e) => setSettings({ ...settings, zomato_markup_pct: e.target.value })}
                className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                data-testid="zomato-markup-pct"
                placeholder="e.g. 30"
              />
              <div className="text-[11px] text-boba-inkSoft mt-1">
                Auto-inflates Base Price → Zomato Price when item has no explicit Zomato Price.
              </div>
            </div>
          </div>
          <button onClick={save} className="mt-4 h-11 px-5 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="settings-save-aggregator">Save</button>
        </div>


        <div className="bg-white border border-boba-stoneLine rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Staff</h2>
            <button onClick={() => setEditing({ name: "", role: "cashier", pin: "" })} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium text-sm flex items-center gap-2" data-testid="add-staff-btn">
              <Plus size={16} weight="bold" /> Add Staff
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Role</th>
                <th className="text-left py-2">Outlet</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="staff-table">
              {staff.map((s) => (
                <tr key={s.id} className="border-t border-boba-stoneLine">
                  <td className="py-3 font-semibold">{s.name}</td>
                  <td className="py-3 capitalize">{s.role.replace("_", " ")}</td>
                  <td className="py-3 text-boba-inkSoft">
                    {s.outlet_id ? (outlets.find((o) => o.id === s.outlet_id)?.name || "—") : "All outlets"}
                  </td>
                  <td className="py-3 text-right">
                    <button onClick={() => setEditing({ ...s, pin: "" })} className="text-sm text-boba-matcha mr-3 hover:underline">Edit</button>
                    <button onClick={() => delStaff(s.id)} className="p-2 text-boba-danger hover:bg-red-50 rounded inline-flex"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="staff-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit Staff" : "New Staff"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="staff-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Role</label>
                  <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white" data-testid="staff-role">
                    <option value="cashier">Cashier</option>
                    <option value="admin">Outlet Admin</option>
                    <option value="super_admin">Super Admin (Master)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">{editing.id ? "New PIN (optional)" : "PIN"}</label>
                  <input type="password" value={editing.pin || ""} onChange={(e) => setEditing({ ...editing, pin: e.target.value })} placeholder="••••" className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="staff-pin" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Outlet</label>
                <select
                  value={editing.outlet_id || ""}
                  onChange={(e) => setEditing({ ...editing, outlet_id: e.target.value || null })}
                  disabled={editing.role === "super_admin"}
                  className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white disabled:bg-stone-50"
                  data-testid="staff-outlet"
                >
                  <option value="">{editing.role === "super_admin" ? "All outlets" : "— Select outlet —"}</option>
                  {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={saveStaff} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="staff-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
